import { supabase } from "@/lib/supabase-client";
import { ProfileType } from "@/types/profiles";
import { Query } from "@/components/Table/types";

export const profileService = {
  /**
   */
  async getProfiles(): Promise<ProfileType[]> {
    const { data, error } = await supabase.from("profiles").select("*");
    // .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }
    return data as ProfileType[];
  },

  async getProfilesByIds(ids: string[]): Promise<ProfileType[] | null> {
    const { data, error } = await supabase.from("profiles").select("*").in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return data as ProfileType[];
  },

  async getProfileById(id: string): Promise<ProfileType | null> {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }
    return data as ProfileType;
  },

  async updateProfileMetaData(id: string, metaData: any): Promise<boolean> {
    const { error } = await supabase.from("profiles").update({ meta_data: metaData }).eq("id", id);
    if (error) {
      console.error("Error updating Profile meta_data:", error.message);
      return false;
    }
    return true;
  },

  async getProfilesFuzzy(search: string): Promise<ProfileType[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,preferred_name.ilike.%${search}%`);

    if (error) {
      throw Error(error.message);
    }

    return data as ProfileType[];
  },
  async updateChats(profileId: string, ids: string[]) {
    const { data, error } = await supabase.from("profiles").update({ chats: ids }).eq("id", profileId);
  },
  async getQueriesByPage<FN extends string = string>(profileId: string, page: string): Promise<Query<FN>[]> {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;
    return row?.meta_data?.queries?.[page] ?? [];
  },

  async deleteSavedQueryByPage(profileId: string, page: string, queryId: string) {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;

    const existingQueries = row?.meta_data?.queries ?? {};
    const pageQueries = existingQueries[page] ?? [];
    const filtered = pageQueries.filter((q) => q.id !== queryId);

    const { error: uErr } = await supabase
      .from("profiles")
      .update({
        meta_data: {
          ...row?.meta_data,
          queries: {
            ...existingQueries,
            [page]: filtered,
          },
        },
      })
      .eq("id", profileId);
    if (uErr) throw uErr;
  },

  async saveQueryByPage<FN extends string = string>(profileId: string, page: string, query: Query<FN>) {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;

    const existingQueries = row?.meta_data?.queries ?? {};
    const pageQueries = existingQueries[page] ?? [];
    const updated = [...pageQueries, { ...query, id: Date.now().toString() }];

    const { error: uErr } = await supabase
      .from("profiles")
      .update({
        meta_data: {
          ...row?.meta_data,
          queries: {
            ...existingQueries,
            [page]: updated,
          },
        },
      })
      .eq("id", profileId);
    if (uErr) throw uErr;
  },

  async addChats(profileId: string, ids: string[]) {
    const { data: row, error: sErr } = await supabase
      .from("profiles")
      .select("chats, updated_at")
      .eq("id", profileId)
      .single();
    if (sErr) throw sErr;

    const next = Array.from(new Set([...(row?.chats ?? []), ...ids]));
    let query = supabase.from("profiles").update({ chats: next }).eq("id", profileId);

    // Only apply optimistic lock if updated_at exists
    if (row.updated_at) {
      query = query.eq("updated_at", row.updated_at);
    }

    const { error: uErr } = await query;
    if (uErr) throw uErr;
  },
  async removeChats(profileId: string, ids: string[]) {
    const { data: row, error: sErr } = await supabase
      .from("profiles")
      .select("chats, updated_at")
      .eq("id", profileId)
      .single();
    if (sErr) throw sErr;

    const drop = new Set(ids);
    const next = (row?.chats ?? []).filter((x: string) => !drop.has(x));
    let query = supabase.from("profiles").update({ chats: next }).eq("id", profileId);

    // Only apply optimistic lock if updated_at exists
    if (row.updated_at) {
      query = query.eq("updated_at", row.updated_at);
    }

    const { error: uErr } = await query;
    if (uErr) throw uErr;
  },

  // Hint management methods
  async getDismissedHints(profileId: string): Promise<Record<string, boolean>> {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;
    return row?.meta_data?.dismissed_hints ?? {};
  },

  async dismissHint(profileId: string, hintId: string): Promise<void> {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;

    const existingHints = row?.meta_data?.dismissed_hints ?? {};

    const { error: uErr } = await supabase
      .from("profiles")
      .update({
        meta_data: {
          ...row?.meta_data,
          dismissed_hints: {
            ...existingHints,
            [hintId]: true,
          },
        },
      })
      .eq("id", profileId);
    if (uErr) throw uErr;
  },

  async resetHint(profileId: string, hintId: string): Promise<void> {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;

    const existingHints = row?.meta_data?.dismissed_hints ?? {};
    delete existingHints[hintId];

    const { error: uErr } = await supabase
      .from("profiles")
      .update({
        meta_data: {
          ...row?.meta_data,
          dismissed_hints: existingHints,
        },
      })
      .eq("id", profileId);
    if (uErr) throw uErr;
  },

  async resetAllHints(profileId: string): Promise<void> {
    const { data: row, error: sErr } = await supabase.from("profiles").select("meta_data").eq("id", profileId).single();
    if (sErr) throw sErr;

    const { error: uErr } = await supabase
      .from("profiles")
      .update({
        meta_data: {
          ...row?.meta_data,
          dismissed_hints: {},
        },
      })
      .eq("id", profileId);
    if (uErr) throw uErr;
  },
};

// export async function updateProfileChats(id: string, chats: string[], retries = 3): Promise<boolean> {
//   if (id === "") {
//     return false;
//   } else {
//   }
//   for (let attempt = 0; attempt < retries; attempt++) {
//     const { data: row, error: selErr } = await supabase.from("profiles").select("meta_data").eq("id", id).single();
//
//     if (selErr) throw new Error(selErr.message);
//     const nextMeta = { ...(row?.meta_data ?? {}), chats: Array.from(new Set(chats)) };
//
//     const { data: updated, error: updErr } = await supabase
//       .from("profiles")
//       .update({ meta_data: nextMeta })
//       .eq("id", id);
//
//     if (updErr) throw new Error(updErr.message);
//     if (updated) return true;
//   }
//   return false;
// }
